import { useMemo, useState } from "react";

import dayjs, { type Dayjs } from "@eisox/dayjs";
import {
  ActionButton,
  Circle,
  Dialog,
  Loader,
  TableV2 as Table,
  type DateRange,
  Typography,
} from "@eisox/design-system";
import { ArrowRightIcon, HistoryIcon, TargetIcon } from "@eisox/icons";
import { useControllableState } from "@radix-ui/react-use-controllable-state";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { flexRender, getCoreRowModel, useReactTable, type ColumnDef } from "@tanstack/react-table";

import { moduleApi } from "~/api";
import { PeriodField } from "~/components/period-field";
import { m } from "~/paraglide/messages";

import {
  compareDefectHistoryByEndDefault,
  filterDefectsByScope,
  toDefectHistoryRows,
  type DefectHistoryRow,
  type DefectScope,
} from "../helpers/defect-history.helper";

/** Legacy `History.BMS`: the calendar reaches two years back and opens on the last two months. */
const MAX_LOOKBACK_YEARS = 2;
const DEFAULT_WINDOW_MONTHS = 2;

const getDefaultRange = (): { from: Dayjs; to: Dayjs } => ({
  from: dayjs().subtract(DEFAULT_WINDOW_MONTHS, "month"),
  to: dayjs(),
});

/* -------------------------------------------------------------------------------------------------
 * View
 * -----------------------------------------------------------------------------------------------*/

interface DefectHistoryDialogViewProps extends Dialog.RootProps {
  rows: DefectHistoryRow[];
  scope: DefectScope;
  /** Building the dialog was opened from, for the location links. */
  buildingId: string;
  /** Boiler room / air handling unit the dialog was opened from — its own rows don't link away. */
  entityId: string;
  pendingRange?: DateRange;
  onPendingRangeChange: (range?: DateRange) => void;
  onApplyRange: () => void;
  isLoading: boolean;
}

export const DefectHistoryDialogView: React.FC<DefectHistoryDialogViewProps> = ({
  rows,
  scope,
  buildingId,
  entityId,
  pendingRange,
  onPendingRangeChange,
  onApplyRange,
  isLoading,
  children,
  ...props
}) => {
  const columns = useMemo<ColumnDef<DefectHistoryRow>[]>(
    () => [
      {
        accessorKey: "begin",
        header: m.begin(),
        cell: ({ getValue }) => {
          const begin = getValue<string | undefined>();
          return begin ? dayjs(begin).format("L LT") : "-";
        },
      },
      {
        accessorKey: "end",
        header: m.end(),
        cell: ({ row: { original } }) =>
          original.end ? (
            dayjs(original.end).format("L LT")
          ) : (
            <div className="gap-1.25 flex items-center">
              <Circle.Root size={10} backgroundColor="red" />
              {m.defect_history_active()}
            </div>
          ),
      },
      {
        accessorKey: "location",
        header: m.defect_history_location(),
        cell: ({ row: { original } }) => {
          const { location, boilerroomId, airHandlerUnitId } = original;
          const targetId = scope === "boilerRoom" ? boilerroomId : airHandlerUnitId;
          if (!location || !targetId) return null;

          const label = (
            <>
              {location}
              <TargetIcon />
            </>
          );

          // Already looking at this equipment — closing the dialog is the whole navigation.
          if (targetId === entityId) {
            return (
              <Dialog.Close asChild>
                <div className="gap-1.25 flex cursor-pointer items-center">{label}</div>
              </Dialog.Close>
            );
          }

          return scope === "boilerRoom" ? (
            <Link
              className="gap-1.25 flex cursor-pointer items-center"
              to="/buildings/$buildingId/boiler-rooms/$boilerRoomId"
              params={{ buildingId, boilerRoomId: targetId }}
            >
              {label}
            </Link>
          ) : (
            <Link
              className="gap-1.25 flex cursor-pointer items-center"
              to="/buildings/$buildingId/air-handler-units/$airHandlerUnitId"
              params={{ buildingId, airHandlerUnitId: targetId }}
            >
              {label}
            </Link>
          );
        },
      },
      { accessorKey: "message", header: m.defect_history_description() },
    ],
    [scope, buildingId, entityId],
  );

  const table = useReactTable({ data: rows, columns, getCoreRowModel: getCoreRowModel() });

  return (
    <Dialog.Root {...props}>
      {children ? <Dialog.Trigger asChild>{children}</Dialog.Trigger> : null}
      <Dialog.Content
        title={m.defect_history_title()}
        icon={<HistoryIcon />}
        className="gap-4 md:gap-6 max-h-225 flex h-[85dvh] w-full flex-col min-[992px]:w-[80%] min-[1200px]:w-[70%]"
      >
        <div className="gap-2.5 flex w-full items-end">
          <div className="gap-1.25 flex flex-1 flex-col">
            <Typography className="text-primary-dark-gray">{m.period()}</Typography>
            <PeriodField
              value={pendingRange}
              onValueChange={onPendingRangeChange}
              min={dayjs().subtract(MAX_LOOKBACK_YEARS, "year")}
              max={dayjs()}
            />
          </div>
          <ActionButton className="px-5 py-3 [&_svg]:h-5 rounded-[10px]" onClick={onApplyRange}>
            <ArrowRightIcon />
          </ActionButton>
        </div>

        <div className="min-h-0 w-full flex-1 overflow-hidden">
          <Table.Container>
            <Table.Root className="w-full table-fixed">
              <colgroup>
                <col className="w-[18%]" />
                <col className="w-[18%]" />
                <col className="w-[26%]" />
                <col className="w-[38%]" />
              </colgroup>
              <Table.Header className="top-0 sticky z-10 bg-primary-white">
                {table.getHeaderGroups().map(headerGroup => (
                  <Table.Row key={headerGroup.id}>
                    {headerGroup.headers.map(header => (
                      <Table.Head key={header.id} className="font-bold py-1.25 text-left">
                        <Typography keepHoverOpen>
                          {flexRender(header.column.columnDef.header, header.getContext())}
                        </Typography>
                      </Table.Head>
                    ))}
                  </Table.Row>
                ))}
              </Table.Header>
              <Table.Body>
                {rows.length === 0 ? (
                  <Table.Row>
                    <Table.Cell colSpan={columns.length}>
                      <div className="py-10 flex items-center justify-center text-primary-dark-gray">
                        {isLoading ? <Loader size={24} /> : <Typography>{m.defect_history_no_data()}</Typography>}
                      </div>
                    </Table.Cell>
                  </Table.Row>
                ) : (
                  table.getRowModel().rows.map(row => (
                    <Table.Row key={row.id}>
                      {row.getVisibleCells().map(cell => (
                        <Table.Cell key={cell.id} className="py-2.5">
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </Table.Cell>
                      ))}
                    </Table.Row>
                  ))
                )}
              </Table.Body>
            </Table.Root>
          </Table.Container>
        </div>
      </Dialog.Content>
    </Dialog.Root>
  );
};

DefectHistoryDialogView.displayName = "DefectHistoryDialogView";

/* -------------------------------------------------------------------------------------------------
 * DefectHistoryDialog
 * -----------------------------------------------------------------------------------------------*/

interface DefectHistoryDialogProps extends Dialog.RootProps {
  buildingId: string;
  scope: DefectScope;
  entityId: string;
}

export const DefectHistoryDialog: React.FC<DefectHistoryDialogProps> = ({
  buildingId,
  scope,
  entityId,
  open: openProp,
  onOpenChange,
  defaultOpen = false,
  children,
  ...props
}) => {
  const [open, setOpen] = useControllableState({ prop: openProp, onChange: onOpenChange, defaultProp: defaultOpen });

  const [range, setRange] = useState<DateRange>(getDefaultRange);
  const [pendingRange, setPendingRange] = useState<DateRange | undefined>(range);

  const { data = [], isLoading } = useQuery({
    ...moduleApi.queries.defectHistory(buildingId, {
      startDate: (range.from ?? getDefaultRange().from).startOf("day").toISOString(),
      endDate: (range.to ?? range.from ?? getDefaultRange().to).endOf("day").toISOString(),
    }),
    enabled: open,
  });

  const rows = useMemo(
    () => toDefectHistoryRows(filterDefectsByScope(data, scope), scope).sort(compareDefectHistoryByEndDefault),
    [data, scope],
  );

  return (
    <DefectHistoryDialogView
      {...props}
      open={open}
      onOpenChange={setOpen}
      rows={rows}
      scope={scope}
      buildingId={buildingId}
      entityId={entityId}
      pendingRange={pendingRange}
      onPendingRangeChange={setPendingRange}
      onApplyRange={() => pendingRange?.from && setRange(pendingRange)}
      isLoading={isLoading}
    >
      {children}
    </DefectHistoryDialogView>
  );
};

DefectHistoryDialog.displayName = "DefectHistoryDialog";
